(() => {
  'use strict';

  const SWATCH_SELECTOR = '.pl-swatches__link';
  const PRODUCT_SELECTOR = [
    '[id^="ProductSection-"]',
    '[id*="ProductSection"]',
    '[data-section-type="product"]',
    '.product-section',
    '#MainContent'
  ].join(',');

  const EXCLUDED_SELECTOR = [
    '.pl-swatches',
    '.complete-the-set',
    '.complete-set',
    '[class*="complete-the-set"]',
    '[id*="complete-the-set"]',
    '.related-products',
    '.product-recommendations',
    '.product-card',
    '.grid-product',
    '.quick-product',
    '.quick-add',
    '[data-product-card]',
    '[data-product-recommendations]'
  ].join(',');

  const SIZE_VALUES = new Set([
    'xxs', 'xs', 's', 'm', 'l', 'xl',
    '2xl', '3xl', '4xl', '5xl', '6xl'
  ]);

  const SIZE_TYPE_VALUES = new Set([
    'regular', 'petite', 'tall'
  ]);

  const productCache = new Map();
  const documentCache = new Map();
  const imageCache = new Map();

  let activeProduct = null;
  let activeHandle = '';
  let activeColor = '';
  let switchToken = 0;
  let internalVariantSync = false;
  let observerFrame = 0;

  const clean = (value) => String(value || '')
    .replace(/\s+/g, ' ')
    .trim();

  const lower = (value) => clean(value).toLowerCase();

  function getProductRoot(sourceDocument = document) {
    return (
      sourceDocument.querySelector(PRODUCT_SELECTOR) ||
      sourceDocument.querySelector('#MainContent') ||
      sourceDocument.body
    );
  }

  function getHandleFromUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      const parts = url.pathname.split('/').filter(Boolean);
      const index = parts.indexOf('products');
      return index >= 0 ? parts[index + 1] || '' : '';
    } catch (error) {
      return '';
    }
  }

  function getCurrentHandle() {
    return getHandleFromUrl(window.location.href);
  }

  function getSwatchHandle(swatch) {
    return (
      swatch?.dataset?.comeniiHandle ||
      swatch?.dataset?.handle ||
      getHandleFromUrl(swatch?.getAttribute?.('href'))
    );
  }

  function getSwatchColor(swatch) {
    if (!swatch) {
      return '';
    }

    const handle =
      getSwatchHandle(swatch);

    const cachedHandle =
      clean(
        swatch.dataset
          .comeniiResolvedHandle
      );

    const cachedColor =
      clean(
        swatch.dataset
          .comeniiResolvedColor
      );

    /*
     * 只有缓存所属 handle 与当前 handle 一致，
     * 才允许使用缓存，避免 DOM 被 App 重用后
     * 继续拿到上一个颜色，例如 Navy。
     */
    if (
      handle &&
      cachedHandle === handle &&
      cachedColor
    ) {
      return cachedColor;
    }

    return clean(
      swatch.getAttribute(
        'title'
      ) ||
      swatch.getAttribute(
        'aria-label'
      ) ||
      swatch.dataset
        .colorName ||
      swatch.dataset
        .color ||
      swatch.dataset
        .value ||
      ''
    );
  }

  function getColorFromDocument(
    sourceDocument
  ) {
    const root =
      getProductRoot(
        sourceDocument
      );

    if (!root) {
      return '';
    }

    const walker =
      sourceDocument.createTreeWalker(
        root,
        NodeFilter.SHOW_TEXT
      );

    let node;

    while (
      (
        node =
          walker.nextNode()
      )
    ) {
      const parent =
        node.parentElement;

      /*
       * 只排除色块链接内部的文字，
       * 不能排除整个 .pl-swatches 容器，
       * 因为 COLOR 名称就在该容器里面。
       */
      if (
        !parent ||
        parent.closest(
          SWATCH_SELECTOR
        )
      ) {
        continue;
      }

      const text =
        clean(
          node.nodeValue
        );

      const match =
        text.match(
          /^color\s*:\s*(.+)$/i
        );

      if (
        match &&
        clean(match[1])
      ) {
        return clean(
          match[1]
        );
      }
    }

    return '';
  }

  function getColorFromProduct(product) {
    const options = Array.isArray(product?.options)
      ? product.options
      : [];

    const colorIndex = options.findIndex((option) => {
      const name = lower(
        typeof option === 'string'
          ? option
          : option?.name || ''
      );

      return (
        name.includes('color') ||
        name.includes('colour')
      );
    });

    const variant =
      product?.variants?.find((item) => item.available) ||
      product?.variants?.[0];

    if (colorIndex >= 0 && variant) {
      return clean(
        variant.options?.[colorIndex] ||
        variant[`option${colorIndex + 1}`] ||
        ''
      );
    }

    return '';
  }

  async function resolveSwatchColor(
    swatch
  ) {
    if (
      !(
        swatch instanceof
        HTMLAnchorElement
      )
    ) {
      return '';
    }

    const handle =
      getSwatchHandle(swatch);

    if (!handle) {
      return '';
    }

    const cachedHandle =
      clean(
        swatch.dataset
          .comeniiResolvedHandle
      );

    const cachedColor =
      clean(
        swatch.dataset
          .comeniiResolvedColor
      );

    if (
      cachedHandle === handle &&
      cachedColor
    ) {
      return cachedColor;
    }

    /*
     * 只读取目标商品 JSON。
     * 不再读取目标页面的 COLOR 文字，
     * 因为该文字可能被色块 App 保留为旧颜色。
     */
    const product =
      await fetchProduct(handle);

    const color =
      getColorFromProduct(
        product
      );

    if (color) {
      swatch.dataset
        .comeniiResolvedHandle =
        handle;

      swatch.dataset
        .comeniiResolvedColor =
        color;
    } else {
      delete swatch.dataset
        .comeniiResolvedHandle;

      delete swatch.dataset
        .comeniiResolvedColor;
    }

    return color;
  }

  function preserveCurrentView(value) {
    const target = new URL(value, window.location.origin);
    const current = new URL(window.location.href);

    if (
      !target.searchParams.has('view') &&
      current.searchParams.has('view')
    ) {
      target.searchParams.set(
        'view',
        current.searchParams.get('view')
      );
    }

    return target;
  }

  function getSwatchUrl(swatch, handle) {
    const root = window.Shopify?.routes?.root || '/';
    const rawHref = swatch?.getAttribute?.('href') || '';
    const hrefHandle = getHandleFromUrl(rawHref);

    const value = rawHref && hrefHandle === handle
      ? rawHref
      : `${root}products/${encodeURIComponent(handle)}`;

    return preserveCurrentView(value).href;
  }

  function normalizeSwatches(root = document) {
    root.querySelectorAll(SWATCH_SELECTOR).forEach((swatch) => {
      if (!(swatch instanceof HTMLAnchorElement)) return;

      const handle = getSwatchHandle(swatch);
      if (!handle) return;

      swatch.dataset.comeniiHandle = handle;
      swatch.href = getSwatchUrl(swatch, handle);
    });
  }

  function productJsonUrl(handle) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}products/${encodeURIComponent(handle)}.js`;
  }

  function fetchProduct(handle) {
    if (!handle) {
      return Promise.reject(new Error('Missing product handle.'));
    }

    if (productCache.has(handle)) {
      return productCache.get(handle);
    }

    const request = fetch(productJsonUrl(handle), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Product request failed: ${response.status}`);
        }

        return response.json();
      })
      .catch((error) => {
        productCache.delete(handle);
        throw error;
      });

    productCache.set(handle, request);

    return request;
  }

  function fetchTargetDocument(url) {
    const targetUrl = preserveCurrentView(url).href;

    if (documentCache.has(targetUrl)) {
      return documentCache.get(targetUrl);
    }

    const request = fetch(targetUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Page request failed: ${response.status}`);
        }

        return response.text();
      })
      .then((html) => {
        return new DOMParser().parseFromString(
          html,
          'text/html'
        );
      })
      .catch((error) => {
        documentCache.delete(targetUrl);
        throw error;
      });

    documentCache.set(targetUrl, request);

    return request;
  }

  function normalizeImageUrl(value) {
    if (!value) return '';

    return value.startsWith('//')
      ? `${window.location.protocol}${value}`
      : value;
  }

  function sizedImageUrl(value, width) {
    const source = normalizeImageUrl(value);

    if (!source) return '';

    try {
      const url = new URL(source);

      url.searchParams.set(
        'width',
        String(width)
      );

      return url.href;
    } catch (error) {
      return source;
    }
  }

  function preloadProductImages(product) {
    (product?.images || [])
      .slice(0, 5)
      .forEach((source, index) => {
        const url = sizedImageUrl(
          source,
          index === 0 ? 1200 : 300
        );

        if (!url || imageCache.has(url)) {
          return;
        }

        const image = new Image();

        image.decoding = 'async';
        image.src = url;

        imageCache.set(url, image);
      });
  }

  function getCurrentColorHeading() {
    const root = getProductRoot();

    if (!root) return '';

    const walker = document.createTreeWalker(
      root,
      NodeFilter.SHOW_TEXT
    );

    let node;

    while ((node = walker.nextNode())) {
      const text = clean(node.nodeValue);

      if (/^color\s*:/i.test(text)) {
        return text
          .replace(/^color\s*:/i, '')
          .trim();
      }
    }

    return '';
  }

  function writeColorHeading(
    color = activeColor
  ) {
    const rawValue =
      clean(color);

    if (!rawValue) {
      return false;
    }

    const value =
      rawValue ===
        rawValue.toLowerCase()
        ? rawValue.replace(
            /\b[a-z]/g,
            (letter) =>
              letter.toUpperCase()
          )
        : rawValue;

    const root =
      getProductRoot();

    if (!root) {
      return false;
    }

    const expected =
      `COLOR: ${value}`;

    const walker =
      document.createTreeWalker(
        root,
        NodeFilter.SHOW_TEXT
      );

    let node;
    let found = false;

    while (
      (
        node =
          walker.nextNode()
      )
    ) {
      const parent =
        node.parentElement;

      if (
        !parent ||
        parent.closest(
          SWATCH_SELECTOR
        )
      ) {
        continue;
      }

      const original =
        String(
          node.nodeValue || ''
        );

      const text =
        clean(original);

      if (
        !/^color\s*:/i.test(
          text
        )
      ) {
        continue;
      }

      found = true;

      const valueAfterColon =
        clean(
          text.replace(
            /^color\s*:/i,
            ''
          )
        );

      if (
        !valueAfterColon &&
        !parent.querySelector(
          SWATCH_SELECTOR
        )
      ) {
        parent.textContent =
          expected;

        continue;
      }

      const leading =
        original.match(
          /^\s*/
        )?.[0] || '';

      const trailing =
        original.match(
          /\s*$/
        )?.[0] || '';

      node.nodeValue =
        `${leading}${expected}${trailing}`;
    }

    if (!found) {
      const candidates =
        Array.from(
          root.querySelectorAll(
            [
              'div',
              'span',
              'p',
              'label',
              'legend',
              'strong',
              'b'
            ].join(',')
          )
        ).filter((element) => {
          if (
            element.closest(
              SWATCH_SELECTOR
            ) ||
            element.querySelector(
              SWATCH_SELECTOR
            )
          ) {
            return false;
          }

          const text =
            clean(
              element.textContent
            );

          return (
            /^color\s*:/i.test(
              text
            ) &&
            text.length < 80
          );
        });

      const deepestCandidates =
        candidates.filter(
          (element) => {
            return !candidates.some(
              (other) => {
                return (
                  other !== element &&
                  element.contains(other)
                );
              }
            );
          }
        );

      deepestCandidates.forEach(
        (element) => {
          element.textContent =
            expected;

          found = true;
        }
      );
    }

    return found;
  }

  function setActiveColor(color) {
    const value = clean(color);

    if (!value) return;

    activeColor = value;

    document.documentElement
      .dataset
      .comeniiActiveColor = value;

    writeColorHeading(value);

    [
      0,
      40,
      100,
      250,
      500,
      900,
      1500
    ].forEach((delay) => {
      window.setTimeout(() => {
        if (activeColor === value) {
          writeColorHeading(value);
        }
      }, delay);
    });
  }

  function markCurrentSwatch(handle) {
    const legacyStateClasses = [
      'active',
      'is-active',
      'selected',
      'is-selected',
      'current',
      'is-current',
      'checked',
      'is-checked',
      'swatch-selected',
      'swatch--selected',
      'pl-swatches__link--active',
      'pl-swatches__link--selected'
    ];

    const clearLegacyState = (element) => {
      if (!(element instanceof Element)) {
        return;
      }

      legacyStateClasses.forEach(
        (className) => {
          element.classList.remove(
            className
          );
        }
      );

      element.removeAttribute(
        'aria-current'
      );

      element.removeAttribute(
        'aria-selected'
      );

      element.removeAttribute(
        'aria-checked'
      );
    };

    document
      .querySelectorAll(SWATCH_SELECTOR)
      .forEach((swatch) => {
        const current =
          getSwatchHandle(swatch) === handle;

        swatch.classList.toggle(
          'comenii-color-current',
          current
        );

        swatch.classList.toggle(
          'comenii-color-inactive',
          !current
        );

        if (current) {
          swatch.setAttribute(
            'aria-current',
            'page'
          );
        } else {
          /*
           * 清掉旧颜色上的 App 默认状态。
           * 避免切换之后灰色圈圈残留。
           */
          clearLegacyState(
            swatch
          );

          swatch
            .querySelectorAll('*')
            .forEach((child) => {
              clearLegacyState(
                child
              );
            });
        }
      });
  }

  function setImageElement(
    image,
    source,
    width,
    alt
  ) {
    if (
      !(image instanceof HTMLImageElement) ||
      !source
    ) {
      return;
    }

    const original =
      normalizeImageUrl(source);

    const sized =
      sizedImageUrl(source, width);

    if (!sized) return;

    image.removeAttribute('srcset');
    image.removeAttribute('data-srcset');

    image.src = sized;

    image.setAttribute(
      'src',
      sized
    );

    image.setAttribute(
      'data-src',
      sized
    );

    image.setAttribute(
      'data-image-url',
      original
    );

    image.setAttribute(
      'data-zoom',
      original
    );

    image.alt = alt || '';

    image.classList.remove(
      'lazyload',
      'lazyloading'
    );

    image.classList.add(
      'lazyloaded'
    );

    image
      .closest('picture')
      ?.querySelectorAll('source')
      .forEach((sourceElement) => {
        sourceElement.srcset = sized;
      });

    const link =
      image.closest('a[href]');

    if (
      link &&
      !link.closest('.pl-swatches')
    ) {
      link.href = original;

      link.setAttribute(
        'data-zoom',
        original
      );

      link.setAttribute(
        'data-photoswipe-src',
        original
      );
    }
  }

  function unique(elements) {
    return Array.from(
      new Set(
        elements.filter(Boolean)
      )
    );
  }

  function getGalleryRoot() {
    const root = getProductRoot();

    return (
      root?.querySelector([
        '.product-single__photos',
        '.product__photos',
        '.product__main-photos',
        '.product-slideshow',
        '.product-gallery',
        '.product-media',
        '[data-product-photos]',
        '[data-product-gallery]',
        '.grid__item.medium-up--three-fifths'
      ].join(',')) ||
      root
    );
  }

  function getGalleryImages() {
    const gallery = getGalleryRoot();

    if (!gallery) {
      return {
        gallery: null,
        main: [],
        thumbs: []
      };
    }

    const thumbSelector = [
      '.product__thumb-item',
      '.product__thumb',
      '.product__thumbs',
      '.product-thumbnails',
      '.product-single__thumbnails',
      '.thumbnail-list',
      '[data-product-thumb]'
    ].join(',');

    const all = Array.from(
      gallery.querySelectorAll('img')
    ).filter((image) => {
      if (
        !(image instanceof HTMLImageElement)
      ) {
        return false;
      }

      if (
        image.closest(EXCLUDED_SELECTOR)
      ) {
        return false;
      }

      return true;
    });

    let thumbs = all.filter((image) => {
      return Boolean(
        image.closest(thumbSelector)
      );
    });

    let main = all.filter((image) => {
      if (thumbs.includes(image)) {
        return false;
      }

      const rect =
        image.getBoundingClientRect();

      return (
        rect.width >= 250 &&
        rect.height >= 250
      );
    });

    if (!main.length) {
      main = all
        .filter((image) => {
          return !thumbs.includes(image);
        })
        .sort((a, b) => {
          const rectA =
            a.getBoundingClientRect();

          const rectB =
            b.getBoundingClientRect();

          return (
            rectB.width *
              rectB.height -
            rectA.width *
              rectA.height
          );
        })
        .slice(0, 1);
    }

    if (!thumbs.length) {
      thumbs = all.filter((image) => {
        if (main.includes(image)) {
          return false;
        }

        const rect =
          image.getBoundingClientRect();

        return (
          rect.width >= 30 &&
          rect.width <= 240
        );
      });
    }

    return {
      gallery,
      main: unique(main),
      thumbs: unique(thumbs)
    };
  }

  function getImageWrapper(
    image,
    type
  ) {
    return image?.closest(
      type === 'thumb'
        ? [
            '.product__thumb-item',
            '[data-product-thumb]',
            '.thumbnail-list li',
            'li'
          ].join(',')
        : [
            '.product-main-slide',
            '.product__photo',
            '.product__media',
            '.product-gallery__image',
            '[data-product-image]',
            '.swiper-slide',
            '.slick-slide'
          ].join(',')
    );
  }

  function resetGalleryPosition(
    gallery,
    mainImages,
    thumbImages
  ) {
    unique(
      mainImages.map((image) => {
        return getImageWrapper(
          image,
          'main'
        );
      })
    ).forEach((wrapper, index) => {
      wrapper.classList.toggle(
        'is-selected',
        index === 0
      );
    });

    unique(
      thumbImages.map((image) => {
        return getImageWrapper(
          image,
          'thumb'
        );
      })
    ).forEach((wrapper, index) => {
      wrapper.classList.toggle(
        'is-active',
        index === 0
      );

      wrapper
        .querySelector('.product__thumb')
        ?.classList.toggle(
          'is-active',
          index === 0
        );
    });

    const flickityElement =
      gallery?.matches?.('.flickity-enabled')
        ? gallery
        : gallery?.querySelector?.(
            '.flickity-enabled'
          );

    if (
      flickityElement &&
      window.Flickity &&
      typeof window.Flickity.data ===
        'function'
    ) {
      const instance =
        window.Flickity.data(
          flickityElement
        );

      if (instance) {
        instance.reloadCells();
        instance.resize();

        instance.select(
          0,
          false,
          true
        );
      }
    }

    const swiperElement =
      gallery?.matches?.('.swiper')
        ? gallery
        : gallery?.querySelector?.(
            '.swiper'
          );

    if (swiperElement?.swiper) {
      swiperElement.swiper.update();

      swiperElement.swiper.slideTo(
        0,
        0,
        false
      );
    }
  }

  function updateGallery(product) {
    const images =
      Array.isArray(product?.images)
        ? product.images
        : [];

    if (!images.length) return;

    const {
      gallery,
      main,
      thumbs
    } = getGalleryImages();

    if (!gallery) return;

    gallery.classList.add(
      'comenii-gallery-loading'
    );

    main.forEach((image, index) => {
      const source = images[index];

      const wrapper =
        getImageWrapper(
          image,
          'main'
        );

      if (!source) {
        if (wrapper) {
          wrapper.style.display =
            'none';
        }

        return;
      }

      wrapper?.style.removeProperty(
        'display'
      );

      setImageElement(
        image,
        source,
        1200,
        product.title
      );
    });

    thumbs.forEach((image, index) => {
      const source = images[index];

      const wrapper =
        getImageWrapper(
          image,
          'thumb'
        );

      if (!source) {
        if (wrapper) {
          wrapper.style.display =
            'none';
        }

        return;
      }

      wrapper?.style.removeProperty(
        'display'
      );

      setImageElement(
        image,
        source,
        180,
        product.title
      );
    });

    resetGalleryPosition(
      gallery,
      main,
      thumbs
    );

    requestAnimationFrame(() => {
      gallery.classList.remove(
        'comenii-gallery-loading'
      );
    });
  }

  function moneyPrefix() {
    const sample =
      getProductRoot()
        ?.querySelector([
          '.product__price.sale-price',
          '.product__price',
          '[data-product-price]'
        ].join(','))
        ?.textContent
        ?.trim() || '$';

    return (
      sample.match(/^[^\d-]+/)?.[0] ||
      '$'
    );
  }

  function formatMoney(cents) {
    return (
      moneyPrefix() +
      (
        Number(cents || 0) /
        100
      ).toLocaleString(
        'en-US',
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }
      )
    );
  }

  function updatePrices(source) {
    const root = getProductRoot();

    if (!root) return;

    const price =
      Number(source?.price || 0);

    const compare =
      Number(
        source?.compare_at_price || 0
      );

    root
      .querySelectorAll([
        '.product__price.sale-price',
        '.avis-li-price',
        '[data-product-price]'
      ].join(','))
      .forEach((element) => {
        element.textContent =
          formatMoney(price);

        element.hidden = false;
      });

    root
      .querySelectorAll([
        '.product__price--compare',
        '.avis-li-compareprice',
        '[data-compare-price]'
      ].join(','))
      .forEach((element) => {
        if (compare > price) {
          element.textContent =
            formatMoney(compare);

          element.hidden = false;

          element.style.removeProperty(
            'display'
          );
        } else {
          element.hidden = true;
        }
      });
  }

  function updateTitle(product) {
    const root = getProductRoot();

    const title =
      root?.querySelector(
        [
          '.product-single__title',
          'h1.product__title',
          'h1'
        ].join(',')
      );

    if (title) {
      title.textContent =
        product.title;
    }

    document.title =
      product.title;
  }

  function getMainProductForm() {
    const forms =
      getProductRoot()
        ?.querySelectorAll(
          'form[action*="/cart/add"]'
        ) || [];

    for (const form of forms) {
      if (
        !(form instanceof HTMLFormElement)
      ) {
        continue;
      }

      if (
        form.closest(EXCLUDED_SELECTOR)
      ) {
        continue;
      }

      if (
        form.querySelector([
          'button[name="add"]',
          'button[type="submit"]',
          '.product-form__submit',
          '.add-to-cart'
        ].join(','))
      ) {
        return form;
      }
    }

    return null;
  }

  function getVariantControls() {
    const form =
      getMainProductForm();

    if (!form) return [];

    let controls = Array.from(
      form.querySelectorAll([
        'input[name="id"]',
        'select[name="id"]'
      ].join(','))
    );

    if (!controls.length) {
      const input =
        document.createElement(
          'input'
        );

      input.type = 'hidden';
      input.name = 'id';

      form.appendChild(input);

      controls = [input];
    }

    return controls;
  }

  function resetVariantControls(
    product
  ) {
    getVariantControls()
      .forEach((control) => {
        if (
          control instanceof
            HTMLSelectElement
        ) {
          control.replaceChildren();

          product.variants.forEach(
            (variant) => {
              const option =
                document.createElement(
                  'option'
                );

              option.value =
                String(variant.id);

              option.textContent =
                variant.title;

              option.disabled =
                !variant.available;

              control.appendChild(
                option
              );
            }
          );

          control.selectedIndex = -1;
        } else {
          control.value = '';

          control.removeAttribute(
            'value'
          );
        }

        control.dataset
          .comeniiProductHandle =
          product.handle;
      });
  }

  function optionName(option) {
    return typeof option === 'string'
      ? option
      : option?.name || '';
  }

  function optionValues(
    product,
    index
  ) {
    const option =
      product?.options?.[index];

    if (
      option &&
      typeof option === 'object' &&
      Array.isArray(option.values)
    ) {
      return option.values;
    }

    return Array.from(
      new Set(
        (product?.variants || [])
          .map((variant) => {
            return variant
              .options?.[index];
          })
          .filter(Boolean)
      )
    );
  }

  function selectedValue(
    allowedValues
  ) {
    const storedSize = lower(
      document.documentElement
        .dataset
        .comeniiSelectedSize
    );

    if (
      allowedValues === SIZE_VALUES &&
      SIZE_VALUES.has(storedSize)
    ) {
      return storedSize;
    }

    const checked = Array.from(
      getProductRoot()
        ?.querySelectorAll(
          'input[type="radio"]:checked'
        ) || []
    ).find((input) => {
      return (
        !input.closest(
          EXCLUDED_SELECTOR
        ) &&
        allowedValues.has(
          lower(input.value)
        )
      );
    });

    return checked
      ? lower(checked.value)
      : '';
  }

  function findSelectedVariant(
    product
  ) {
    const size =
      selectedValue(SIZE_VALUES);

    const sizeType =
      selectedValue(
        SIZE_TYPE_VALUES
      );

    const expected =
      (product.options || [])
        .map((option, index) => {
          const name =
            lower(optionName(option));

          const values =
            optionValues(
              product,
              index
            );

          if (
            name.includes(
              'size type'
            ) ||
            name.includes(
              'size_type'
            )
          ) {
            return sizeType || null;
          }

          if (
            name === 'size' ||
            (
              name.includes('size') &&
              !name.includes('type')
            )
          ) {
            return size || null;
          }

          return values[0] || null;
        });

    if (
      expected.some(
        (value) => !value
      )
    ) {
      return null;
    }

    return (
      product.variants.find(
        (variant) => {
          return variant.options.every(
            (value, index) => {
              return (
                lower(value) ===
                lower(expected[index])
              );
            }
          );
        }
      ) || null
    );
  }

  function getBuyButton() {
    const form =
      getMainProductForm();

    if (!form) return null;

    return (
      Array.from(
        form.querySelectorAll([
          'button[name="add"]',
          'button[type="submit"]',
          '.product-form__submit',
          '.add-to-cart'
        ].join(','))
      ).find((button) => {
        const style =
          getComputedStyle(button);

        return (
          style.display !== 'none' &&
          style.visibility !== 'hidden'
        );
      }) || null
    );
  }

  function setButtonText(text) {
    const button =
      getBuyButton();

    if (!button) return;

    const walker =
      document.createTreeWalker(
        button,
        NodeFilter.SHOW_TEXT
      );

    let node;
    let fallback = null;

    while ((node = walker.nextNode())) {
      if (!node.nodeValue.trim()) {
        continue;
      }

      fallback ||= node;

      if (
        /sold out|add to cart|add to bag|select a size|unavailable/i
          .test(node.nodeValue)
      ) {
        node.nodeValue = text;

        return;
      }
    }

    if (fallback) {
      fallback.nodeValue = text;
    } else {
      button.textContent = text;
    }
  }

  function syncSelectedVariant() {
    if (
      !activeProduct ||
      internalVariantSync
    ) {
      return null;
    }

    const variant =
      findSelectedVariant(
        activeProduct
      );

    if (!variant) return null;

    internalVariantSync = true;

    getVariantControls()
      .forEach((control) => {
        const id =
          String(variant.id);

        if (
          control instanceof
            HTMLSelectElement
        ) {
          control.value = id;

          Array.from(
            control.options
          ).forEach((option) => {
            option.selected =
              option.value === id;
          });
        } else {
          control.value = id;

          control.setAttribute(
            'value',
            id
          );
        }
      });

    internalVariantSync = false;

    updatePrices(variant);

    const button =
      getBuyButton();

    if (button) {
      button.disabled =
        !variant.available;

      if (variant.available) {
        button.removeAttribute(
          'disabled'
        );
      } else {
        button.setAttribute(
          'disabled',
          'disabled'
        );
      }

      button.setAttribute(
        'aria-disabled',
        variant.available
          ? 'false'
          : 'true'
      );

      setButtonText(
        variant.available
          ? 'ADD TO BAG'
          : 'SOLD OUT'
      );
    }

    return variant;
  }

  function scheduleVariantSync() {
    [
      0,
      80,
      250,
      600
    ].forEach((delay) => {
      window.setTimeout(
        syncSelectedVariant,
        delay
      );
    });
  }

  function findCompleteSetRoot(
    sourceDocument
  ) {
    const direct =
      sourceDocument.querySelector([
        '.complete-the-set',
        '.complete-set',
        '[class*="complete-the-set"]',
        '[id*="complete-the-set"]'
      ].join(','));

    if (direct) return direct;

    const heading = Array.from(
      sourceDocument.querySelectorAll([
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'strong',
        'b',
        'div',
        'span'
      ].join(','))
    ).find((element) => {
      return (
        lower(element.textContent) ===
        'complete the set'
      );
    });

    if (!heading) return null;

    let current =
      heading.parentElement;

    for (
      let depth = 0;
      current && depth < 9;
      depth += 1
    ) {
      const text =
        lower(current.textContent);

      const imageCount =
        current.querySelectorAll(
          'img'
        ).length;

      if (
        text.includes(
          'complete the set'
        ) &&
        imageCount >= 2
      ) {
        return current;
      }

      current =
        current.parentElement;
    }

    return null;
  }

  function syncCompleteSetAttributes(
    currentRoot,
    targetRoot
  ) {
    if (
      !currentRoot ||
      !targetRoot
    ) {
      return false;
    }

    const currentImages =
      Array.from(
        currentRoot.querySelectorAll(
          'img'
        )
      );

    const targetImages =
      Array.from(
        targetRoot.querySelectorAll(
          'img'
        )
      );

    const currentLinks =
      Array.from(
        currentRoot.querySelectorAll(
          'a[href]'
        )
      );

    const targetLinks =
      Array.from(
        targetRoot.querySelectorAll(
          'a[href]'
        )
      );

    if (!targetImages.length) {
      return false;
    }

    currentImages.forEach(
      (image, index) => {
        const target =
          targetImages[index];

        if (!target) return;

        const src =
          target.getAttribute('src') ||
          target.currentSrc ||
          target.src;

        if (src) {
          image.src = src;

          image.setAttribute(
            'src',
            src
          );

          image.setAttribute(
            'data-src',
            target.getAttribute(
              'data-src'
            ) || src
          );
        }

        const srcset =
          target.getAttribute(
            'srcset'
          );

        if (srcset) {
          image.setAttribute(
            'srcset',
            srcset
          );
        } else {
          image.removeAttribute(
            'srcset'
          );
        }

        image.alt =
          target.alt || '';
      }
    );

    currentLinks.forEach(
      (link, index) => {
        const target =
          targetLinks[index];

        if (target?.href) {
          link.href = target.href;
        }
      }
    );

    return true;
  }

  async function syncCompleteSet(
    url,
    token
  ) {
    try {
      const targetDocument =
        await fetchTargetDocument(url);

      if (token !== switchToken) {
        return;
      }

      const currentRoot =
        findCompleteSetRoot(document);

      const targetRoot =
        findCompleteSetRoot(
          targetDocument
        );

      syncCompleteSetAttributes(
        currentRoot,
        targetRoot
      );
    } catch (error) {
      // 找不到时不影响主商品。
    }
  }

  async function switchProduct({
    handle,
    color,
    url,
    pushHistory = true
  }) {
    if (!handle) return;

    const token =
      ++switchToken;

    const previousHandle =
      activeHandle ||
      getCurrentHandle();

    const previousColor =
      activeColor ||
      getCurrentColorHeading();

    const targetUrl =
      url ||
      getSwatchUrl(
        null,
        handle
      );

    if (color) {
      setActiveColor(color);
    }

    markCurrentSwatch(handle);

    try {
      const [
        product,
        targetDocument
      ] = await Promise.all([
        fetchProduct(handle),

        fetchTargetDocument(
          targetUrl
        ).catch(() => null)
      ]);

      if (token !== switchToken) {
        return;
      }

      activeProduct = product;

      activeHandle =
        product.handle ||
        handle;

      document.documentElement
        .dataset
        .comeniiActiveHandle =
        activeHandle;

      preloadProductImages(product);
      updateTitle(product);
      updatePrices(product);
      updateGallery(product);
      resetVariantControls(product);

      /*
       * 商品 JSON 是颜色名称唯一权威来源。
       * 点击时取得的 color 只作为备用。
       */
      const resolvedColor =
        getColorFromProduct(
          product
        ) ||
        clean(color);

      if (resolvedColor) {
        setActiveColor(
          resolvedColor
        );

        const matchingSwatch =
          Array.from(
            document.querySelectorAll(
              SWATCH_SELECTOR
            )
          ).find((item) => {
            return (
              getSwatchHandle(item) ===
              activeHandle
            );
          });

        if (matchingSwatch) {
          matchingSwatch.dataset
            .comeniiResolvedHandle =
            activeHandle;

          matchingSwatch.dataset
            .comeniiResolvedColor =
            resolvedColor;
        }
      }

      if (pushHistory) {
        const target =
          preserveCurrentView(
            targetUrl
          );

        history.pushState(
          {
            comeniiInstantProduct:
              handle
          },
          '',
          target.pathname +
            target.search
        );
      }

      document.dispatchEvent(
        new CustomEvent(
          'comenii:instant-product-switch',
          {
            detail: {
              product,
              color: resolvedColor,
              url: targetUrl
            }
          }
        )
      );

      void syncCompleteSet(
        targetUrl,
        token
      );
    } catch (error) {
      console.error(
        '[COMENII instant color]',
        error
      );

      markCurrentSwatch(
        previousHandle
      );

      setActiveColor(
        previousColor
      );
    }
  }

  function getSwatchFromTarget(
    target
  ) {
    if (
      !(target instanceof Element)
    ) {
      return null;
    }

    return target.closest(
      SWATCH_SELECTOR
    );
  }

  function handleSwatchClick(event) {
    if (
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const swatch =
      getSwatchFromTarget(
        event.target
      );

    if (!swatch) return;

    const handle =
      getSwatchHandle(swatch);

    if (!handle) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const current =
      activeHandle ||
      getCurrentHandle();

    if (current === handle) {
      markCurrentSwatch(handle);

      const currentColor =
        getSwatchColor(swatch);

      if (currentColor) {
        setActiveColor(
          currentColor
        );
      }

      return;
    }

    resolveSwatchColor(swatch)
      .then((resolvedColor) => {
        switchProduct({
          handle,

          color:
            resolvedColor ||
            getSwatchColor(swatch),

          url:
            getSwatchUrl(
              swatch,
              handle
            ),

          pushHistory: true
        });
      })
      .catch(() => {
        switchProduct({
          handle,

          color:
            getSwatchColor(swatch),

          url:
            getSwatchUrl(
              swatch,
              handle
            ),

          pushHistory: true
        });
      });
  }

  function warmSwatch(swatch) {
    const handle =
      getSwatchHandle(swatch);

    if (!handle) return;

    void resolveSwatchColor(swatch);

    fetchProduct(handle)
      .then(preloadProductImages)
      .catch(() => {});

    fetchTargetDocument(
      getSwatchUrl(
        swatch,
        handle
      )
    ).catch(() => {});
  }

  function warmAllProducts() {
    const swatches = Array.from(
      document.querySelectorAll(
        SWATCH_SELECTOR
      )
    );

    let index = 0;

    async function worker() {
      while (
        index < swatches.length
      ) {
        const swatch =
          swatches[index++];

        const handle =
          getSwatchHandle(swatch);

        if (
          !handle ||
          handle === activeHandle
        ) {
          continue;
        }

        void resolveSwatchColor(
          swatch
        );

        try {
          const product =
            await fetchProduct(handle);

          preloadProductImages(
            product
          );
        } catch (error) {
          // 点击时重试。
        }
      }
    }

    Promise.allSettled([
      worker(),
      worker()
    ]);
  }

  function installStyles() {
    document
      .getElementById(
        'comenii-instant-color-style'
      )
      ?.remove();

    const style =
      document.createElement(
        'style'
      );

    style.id =
      'comenii-instant-color-style';

    style.textContent = `
      /*
       * 当前颜色只保留我们的黑色方框。
       */
      .pl-swatches__link.comenii-color-current {
        outline: 2px solid #000000 !important;
        outline-offset: 2px !important;
        box-shadow: none !important;
        transform: none !important;
        scale: 1 !important;
      }

      /*
       * 非当前颜色恢复普通状态。
       * 清除旧默认色卡残留的灰圈、阴影、
       * selected 状态视觉和放大效果。
       */
      .pl-swatches__link.comenii-color-inactive {
        outline: none !important;
        outline-offset: 0 !important;
        box-shadow: none !important;
        transform: none !important;
        scale: 1 !important;
        filter: none !important;
      }

      .pl-swatches__link.comenii-color-inactive *,
      .pl-swatches__link.comenii-color-inactive::before,
      .pl-swatches__link.comenii-color-inactive::after,
      .pl-swatches__link.comenii-color-inactive *::before,
      .pl-swatches__link.comenii-color-inactive *::after {
        outline: none !important;
        box-shadow: none !important;
        transform: none !important;
        scale: 1 !important;
        filter: none !important;
      }

      .comenii-gallery-loading img {
        transition: opacity 0.1s ease;
      }
    `;

    document.head.appendChild(style);
  }

  function startObserver() {
    const observer =
      new MutationObserver(() => {
        if (observerFrame) return;

        observerFrame =
          requestAnimationFrame(() => {
            observerFrame = 0;

            normalizeSwatches();

            /*
             * 色卡 App 有可能在切换完成之后，
             * 再把旧颜色的 selected / active
             * class 写回来。
             *
             * 因此 DOM 状态变化后再次校正。
             */
            const currentHandle =
              activeHandle ||
              getCurrentHandle();

            if (currentHandle) {
              markCurrentSwatch(
                currentHandle
              );
            }

            if (activeColor) {
              writeColorHeading(
                activeColor
              );
            }
          });
      });

    observer.observe(
      getProductRoot() ||
        document.body,
      {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: [
          'class',
          'aria-current',
          'aria-selected',
          'aria-checked'
        ]
      }
    );
  }

  function initializeCurrentProduct() {
    activeHandle =
      getCurrentHandle();

    activeColor =
      getCurrentColorHeading();

    if (!activeHandle) return;

    fetchProduct(activeHandle)
      .then((product) => {
        activeProduct = product;

        preloadProductImages(
          product
        );
      })
      .catch(() => {});
  }

  function initialize() {
    installStyles();
    normalizeSwatches();
    initializeCurrentProduct();

    markCurrentSwatch(
      activeHandle ||
      getCurrentHandle()
    );

    document.addEventListener(
      'pointerdown',
      (event) => {
        const swatch =
          getSwatchFromTarget(
            event.target
          );

        if (!swatch) return;

        event.stopPropagation();
        event.stopImmediatePropagation();

        warmSwatch(swatch);
      },
      true
    );

    document.addEventListener(
      'click',
      handleSwatchClick,
      true
    );

    document.addEventListener(
      'pointerover',
      (event) => {
        const swatch =
          getSwatchFromTarget(
            event.target
          );

        if (swatch) {
          warmSwatch(swatch);
        }
      },
      true
    );

    document.addEventListener(
      'touchstart',
      (event) => {
        const swatch =
          getSwatchFromTarget(
            event.target
          );

        if (swatch) {
          warmSwatch(swatch);
        }
      },
      {
        capture: true,
        passive: true
      }
    );

    document.addEventListener(
      'comenii:size-selected',
      (event) => {
        document.documentElement
          .dataset
          .comeniiSelectedSize =
          lower(event.detail?.size);

        scheduleVariantSync();
      }
    );

    document.addEventListener(
      'change',
      (event) => {
        const input =
          event.target;

        if (
          input instanceof
            HTMLInputElement &&
          input.type === 'radio'
        ) {
          const value =
            lower(input.value);

          if (
            SIZE_VALUES.has(value) ||
            SIZE_TYPE_VALUES.has(value)
          ) {
            scheduleVariantSync();
          }
        }
      },
      true
    );

    document.addEventListener(
      'click',
      (event) => {
        if (
          !activeProduct ||
          !(event.target instanceof Element)
        ) {
          return;
        }

        const button =
          getBuyButton();

        if (!button) return;

        if (
          event.target.closest(
            'button,[role="button"]'
          ) !== button
        ) {
          return;
        }

        if (
          document.documentElement
            .dataset
            .comeniiSizeSelected !==
          'true'
        ) {
          return;
        }

        if (!syncSelectedVariant()) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
        }
      },
      true
    );

    document.addEventListener(
      'submit',
      (event) => {
        if (
          !activeProduct ||
          event.target !==
            getMainProductForm()
        ) {
          return;
        }

        if (
          document.documentElement
            .dataset
            .comeniiSizeSelected !==
          'true'
        ) {
          return;
        }

        if (!syncSelectedVariant()) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
        }
      },
      true
    );

    window.addEventListener(
      'popstate',
      () => {
        const handle =
          getCurrentHandle();

        if (!handle) return;

        const swatch =
          Array.from(
            document.querySelectorAll(
              SWATCH_SELECTOR
            )
          ).find((element) => {
            return (
              getSwatchHandle(element) ===
              handle
            );
          });

        const runSwitch = (
          color
        ) => {
          switchProduct({
            handle,

            color,

            url:
              window.location.href,

            pushHistory: false
          });
        };

        if (swatch) {
          resolveSwatchColor(swatch)
            .then((color) => {
              runSwitch(
                color ||
                getSwatchColor(swatch)
              );
            })
            .catch(() => {
              runSwitch(
                getSwatchColor(swatch)
              );
            });
        } else {
          runSwitch('');
        }
      }
    );

    startObserver();

    if (
      'requestIdleCallback' in window
    ) {
      window.requestIdleCallback(
        warmAllProducts,
        {
          timeout: 2500
        }
      );
    } else {
      window.setTimeout(
        warmAllProducts,
        1500
      );
    }
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      {
        once: true
      }
    );
  } else {
    initialize();
  }
})();